import React from "react";
import {
  AlertCircle,
  CheckCircle2,
  Download,
  FileCheck2,
  FileText,
  LoaderCircle,
  ShieldCheck,
  Trash2,
  UploadCloud,
} from "lucide-react";
import { useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth0 } from "@auth0/auth0-react";
import "./LenderManualLoanCaseDocuments.css";
import {
  uploadManualLoanCaseDocument,
  deleteManualLoanCaseDocument,
  fetchLenderDocumentBlob,
} from "./LenderManualLoanCaseApi";

const APPLICANT_DOCUMENT_DEFINITION = {
  code: "income_eligibility_certificate",
  titleKey: "documents.incomeEligibilityCertificate.title",
  descriptionKey: "documents.incomeEligibilityCertificate.description",
  purposeKeys: ["documents.incomeEligibilityCertificate.purposes.eligibility"],
  scope: "APPLICANT",
};

const APPLICATION_DOCUMENT_DEFINITIONS = [
  {
    code: "epc",
    titleKey: "documents.energyPerformanceCertificate.title",
    descriptionKey: "documents.energyPerformanceCertificate.description",
    purposeKeys: [
      "documents.energyPerformanceCertificate.purposes.renovationSupport",
    ],
    scope: "APPLICATION",
  },
  {
    code: "electricity_installation_certificate",
    titleKey: "documents.electricityCertificate.title",
    descriptionKey: "documents.electricityCertificate.description",
    purposeKeys: [
      "documents.electricityCertificate.purposes.renovationSupport",
    ],
    scope: "APPLICATION",
  },
];
function isObject(value) {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}
function getActiveVersion(requirement) {
  if (!requirement) {
    return null;
  }
  return (
    requirement.active_version ||
    requirement.version ||
    requirement.versions?.find((version) => version.is_active) ||
    requirement.versions?.[0] ||
    null
  );
}
const DOCUMENT_CODE_ALIASES = {
  current_energy_performance_certificate: "epc",
  energy_performance_certificate: "epc",
  epc: "epc",

  income_eligibility_certificate: "income_eligibility_certificate",

  disability_certificate: "income_eligibility_certificate",

  electricity_installation_certificate: "electricity_installation_certificate",
};
function normalizeDocumentCode(value) {
  const code = String(value || "")
    .trim()
    .toLowerCase();
  return DOCUMENT_CODE_ALIASES[code] || code;
}
function normalizeDocumentRecord(record, fallbackCode = "") {
  if (!record) {
    return null;
  }
  const activeVersion = getActiveVersion(record);
  const version = activeVersion || record;
  const documentTypeCode = normalizeDocumentCode(
    record.document_type_code ||
      record.document_type?.code ||
      record.code ||
      version.document_type_code ||
      fallbackCode,
  );
  const originalFilename =
    version.original_filename ||
    version.filename ||
    version.file_name ||
    record.original_filename ||
    record.filename ||
    "";
  const versionId =
    record.version_id ||
    record.document_version_id ||
    activeVersion?.id ||
    null;
  const documentId = record.document_id || record.id || null;
  const uploadedAt = version.uploaded_at || record.uploaded_at || null;
  const hasUploadedFile = Boolean(
    originalFilename || versionId || activeVersion || uploadedAt,
  );
  if (!documentTypeCode || !hasUploadedFile) {
    return null;
  }
  return {
    document_type_code: documentTypeCode,
    document_id: documentId,
    version_id: versionId,
    original_filename: originalFilename,
    mime_type: version.mime_type || record.mime_type || "",
    file_size_bytes: version.file_size_bytes || record.file_size_bytes || null,
    uploaded_at: uploadedAt,
    dossier_applicant_id:
      record.dossier_applicant_id ||
      record.dossier_applicant?.id ||
      version.dossier_applicant_id ||
      null,
    applicant_role: String(
      record.applicant_role ||
        record.dossier_applicant?.applicant_role ||
        version.applicant_role ||
        "",
    ).toUpperCase(),
    scope: String(
      record.scope || record.document_type?.scope || version.scope || "",
    ).toUpperCase(),
  };
}

function getApplicantDocumentKey(_applicantId, applicantRole) {
  const role = String(applicantRole || "PRIMARY")
    .trim()
    .toUpperCase();

  return `${role}:${APPLICANT_DOCUMENT_DEFINITION.code}`;
}

function getApplicationDocumentKey(documentTypeCode) {
  return `APPLICATION:${normalizeDocumentCode(documentTypeCode)}`;
}

function normalizeDocumentCollection(source, primaryApplicantId = null) {
  if (!source) return {};
  let records = [];
  if (Array.isArray(source)) records = source;
  else if (Array.isArray(source.uploaded_documents))
    records = source.uploaded_documents;
  else if (Array.isArray(source.items)) records = source.items;
  else if (Array.isArray(source.requirements)) records = source.requirements;
  else if (isObject(source)) {
    records = Object.entries(source).map(([code, value]) => ({
      ...(isObject(value) ? value : {}),
      document_type_code: value?.document_type_code || code,
    }));
  }
  return records.reduce((result, record) => {
    const normalized = normalizeDocumentRecord(
      record,
      record?.document_type_code,
    );
    if (!normalized) return result;
    const code = normalizeDocumentCode(normalized.document_type_code);
    if (code === APPLICANT_DOCUMENT_DEFINITION.code) {
      const applicantId =
        normalized.dossier_applicant_id ||
        (normalized.applicant_role === "CO_APPLICANT"
          ? null
          : primaryApplicantId);
      const applicantRole = normalized.applicant_role || "PRIMARY";
      result[getApplicantDocumentKey(applicantId, applicantRole)] = {
        ...normalized,
        document_type_code: code,
        dossier_applicant_id: applicantId,
        applicant_role: applicantRole,
        scope: "APPLICANT",
      };
      return result;
    }
    result[getApplicationDocumentKey(code)] = {
      ...normalized,
      document_type_code: code,
      dossier_applicant_id: null,
      applicant_role: "",
      scope: "APPLICATION",
    };
    return result;
  }, {});
}

function formatFileSize(value) {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return "";
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
function formatUploadedDate(value, locale = "en-GB") {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}
function serializeUploadedDocuments(uploadedDocuments) {
  return Object.values(uploadedDocuments)
    .filter(Boolean)
    .map((document) => ({
      document_type_code: document.document_type_code,
      document_id: document.document_id || null,
      version_id: document.version_id || null,
      original_filename: document.original_filename || "",
      mime_type: document.mime_type || "",
      file_size_bytes: document.file_size_bytes || null,
      uploaded_at: document.uploaded_at || null,
      dossier_applicant_id: document.dossier_applicant_id || null,
      applicant_role: document.applicant_role || "",
      scope: document.scope || "",
    }));
}

function DocumentUploadCard({
  slot,
  definition,
  document,
  isUploading,
  isDeleting,
  error,
  onUpload,
  onDownload,
  onRemove,
  locale,
  t,
}) {
  const inputId = `manual-document-${slot.key.replace(/[^a-zA-Z0-9_-]/g, "-")}`;
  const isBusy = isUploading || isDeleting;
  const formattedSize = formatFileSize(document?.file_size_bytes);
  const formattedDate = formatUploadedDate(document?.uploaded_at, locale);
  function handleFileChange(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) {
      return;
    }
    onUpload(slot, file);
  }
  return (
    <article
      className={`lender-manual-documents__card ${
        document ? "lender-manual-documents__card--uploaded" : ""
      }`}
    >
      <div className="lender-manual-documents__card-main">
        <div className="lender-manual-documents__document-icon">
          {document ? (
            <CheckCircle2 size={22} strokeWidth={2.2} />
          ) : (
            <FileText size={22} strokeWidth={2} />
          )}
        </div>
        <div className="lender-manual-documents__document-copy">
          <div className="lender-manual-documents__document-heading">
            <h3>
              {t(definition.titleKey)}
              {slot.ownerLabel ? ` — ${slot.ownerLabel}` : ""}
            </h3>
            <span className="lender-manual-documents__mandatory-chip">
              {t("labels.mandatory", "Mandatory")}
            </span>
          </div>
          <p className="lender-manual-documents__description">
            {t(definition.descriptionKey)}
          </p>
          <div className="lender-manual-documents__purposes">
            {definition.purposeKeys.map((purposeKey) => (
              <span
                key={purposeKey}
                className="lender-manual-documents__purpose-chip"
              >
                <ShieldCheck size={13} strokeWidth={2.2} />
                {t(purposeKey)}
              </span>
            ))}
          </div>
        </div>
      </div>
      <div className="lender-manual-documents__upload-area">
        {document ? (
          <div className="lender-manual-documents__uploaded-file">
            <div className="lender-manual-documents__uploaded-file-icon">
              <FileCheck2 size={20} strokeWidth={2.1} />
            </div>
            <div className="lender-manual-documents__uploaded-file-copy">
              <div
                className="lender-manual-documents__filename"
                title={document.original_filename}
              >
                {document.original_filename ||
                  t("status.uploadedDocument", "Uploaded document")}
              </div>
              <div className="lender-manual-documents__file-meta">
                {formattedSize ? <span>{formattedSize}</span> : null}
                {formattedDate ? <span>{formattedDate}</span> : null}
                <span className="lender-manual-documents__uploaded-status">
                  {t("status.uploaded", "Uploaded")}
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div className="lender-manual-documents__empty-file">
            <UploadCloud size={21} strokeWidth={2} />
            <div>
              <strong>{t("upload.noFile", "No document uploaded")}</strong>
              <span>
                {t("upload.acceptedFormats", "Upload a PDF, JPG or PNG file.")}
              </span>
            </div>
          </div>
        )}
        {error ? (
          <div className="lender-manual-documents__upload-error" role="alert">
            <AlertCircle size={15} />
            <span>{error}</span>
          </div>
        ) : null}
        <div className="lender-manual-documents__actions">
          {document?.version_id ? (
            <button
              type="button"
              className="lender-manual-documents__action-btn lender-manual-documents__action-btn--secondary"
              onClick={() => onDownload(document)}
              disabled={isBusy}
            >
              <Download size={15} />
              {t("buttons.download", "Download")}
            </button>
          ) : null}
          <input
            id={inputId}
            className="lender-manual-documents__file-input"
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
            onChange={handleFileChange}
            disabled={isBusy}
          />
          <label
            htmlFor={inputId}
            className={`lender-manual-documents__action-btn lender-manual-documents__action-btn--upload ${
              isBusy ? "lender-manual-documents__action-btn--disabled" : ""
            }`}
            aria-disabled={isBusy}
          >
            {isUploading ? (
              <LoaderCircle
                size={16}
                className="lender-manual-documents__spinner"
              />
            ) : (
              <UploadCloud size={16} />
            )}
            {isUploading
              ? t("buttons.uploading", "Uploading...")
              : document
                ? t("buttons.replace", "Replace")
                : t("buttons.upload", "Upload document")}
          </label>
          {document ? (
            <button
              type="button"
              className="lender-manual-documents__action-btn lender-manual-documents__action-btn--remove"
              onClick={() => onRemove(slot, document)}
              disabled={isBusy}
            >
              {isDeleting ? (
                <LoaderCircle
                  size={15}
                  className="lender-manual-documents__spinner"
                />
              ) : (
                <Trash2 size={15} />
              )}
              {isDeleting
                ? t("buttons.removing", "Removing...")
                : t("buttons.remove", "Remove")}
            </button>
          ) : null}
        </div>
      </div>
    </article>
  );
}
export default function LenderManualLoanCaseDocuments() {
  const { t, i18n } = useTranslation("lender-documents");
  const { getAccessTokenSilently } = useAuth0();
  const {
    loanCaseData,
    loanCaseId,
    currentStepKey,
    stepValues,
    handleStepValidityChange,
    handleStepValuesChange,
  } = useOutletContext();
  const personalStep = stepValues?.["personal-info"] || {};
  const storedPersonalInfo = personalStep.personal_info || personalStep;

  const backendPrimaryApplicant =
    loanCaseData?.personal_info?.primary_applicant ||
    loanCaseData?.primary_applicant ||
    null;

  const storedPrimaryApplicant = storedPersonalInfo?.primary_applicant || null;

  const primaryApplicant = storedPrimaryApplicant
    ? {
        ...(backendPrimaryApplicant || {}),
        ...storedPrimaryApplicant,

        id: storedPrimaryApplicant.id ?? backendPrimaryApplicant?.id ?? null,
      }
    : backendPrimaryApplicant;

  const personalStepHasCoApplicant =
    storedPersonalInfo &&
    typeof storedPersonalInfo === "object" &&
    Object.prototype.hasOwnProperty.call(storedPersonalInfo, "co_applicant");

  const backendCoApplicant =
    loanCaseData?.personal_info?.co_applicant ??
    loanCaseData?.co_applicant ??
    null;

  const storedCoApplicant = storedPersonalInfo?.co_applicant ?? null;

  const coApplicant = personalStepHasCoApplicant
    ? storedCoApplicant
      ? {
          ...(backendCoApplicant || {}),
          ...storedCoApplicant,

          id: storedCoApplicant.id ?? backendCoApplicant?.id ?? null,
        }
      : null
    : backendCoApplicant;

  const primaryApplicantLabel =
    [primaryApplicant?.first_name, primaryApplicant?.last_name]
      .filter(Boolean)
      .join(" ") || t("labels.primaryApplicant", "Primary applicant");
  const coApplicantLabel =
    [coApplicant?.first_name, coApplicant?.last_name]
      .filter(Boolean)
      .join(" ") || t("labels.coBorrower", "Co-borrower");
  const requiredDocumentSlots = React.useMemo(() => {
    const slots = [
      {
        key: getApplicantDocumentKey(primaryApplicant?.id, "PRIMARY"),
        definition: APPLICANT_DOCUMENT_DEFINITION,
        dossierApplicantId: primaryApplicant?.id || null,
        applicantRole: "PRIMARY",
        ownerLabel: primaryApplicantLabel,
        group: "APPLICANT",
      },
    ];
    if (coApplicant) {
      slots.push({
        key: getApplicantDocumentKey(coApplicant?.id, "CO_APPLICANT"),
        definition: APPLICANT_DOCUMENT_DEFINITION,
        dossierApplicantId: coApplicant?.id || null,
        applicantRole: "CO_APPLICANT",
        ownerLabel: coApplicantLabel,
        group: "APPLICANT",
      });
    }
    APPLICATION_DOCUMENT_DEFINITIONS.forEach((definition) => {
      slots.push({
        key: getApplicationDocumentKey(definition.code),
        definition,
        dossierApplicantId: null,
        applicantRole: "",
        ownerLabel: "",
        group: "APPLICATION",
      });
    });
    return slots;
  }, [
    coApplicant,
    coApplicant?.id,
    coApplicantLabel,
    primaryApplicant?.id,
    primaryApplicantLabel,
  ]);
  const applicantDocumentSlots = requiredDocumentSlots.filter(
    (slot) => slot.group === "APPLICANT",
  );
  const applicationDocumentSlots = requiredDocumentSlots.filter(
    (slot) => slot.group === "APPLICATION",
  );
  const [uploadedDocuments, setUploadedDocuments] = React.useState({});
  const [uploadingCode, setUploadingCode] = React.useState("");
  const [deletingCode, setDeletingCode] = React.useState("");
  const [documentErrors, setDocumentErrors] = React.useState({});
  React.useEffect(() => {
    if (!loanCaseData || !loanCaseId) {
      return;
    }
    const backendSource =
      loanCaseData.documents?.uploaded_documents ??
      loanCaseData.documents ??
      loanCaseData.dossier?.documents ??
      [];
    setUploadedDocuments(
      normalizeDocumentCollection(backendSource, primaryApplicant?.id || null),
    );
  }, [loanCaseData, loanCaseId, primaryApplicant?.id]);
  React.useEffect(() => {
    if (coApplicant) {
      return;
    }

    const removedCoApplicantId = backendCoApplicant?.id || null;

    setUploadedDocuments((previous) => {
      let changed = false;
      const next = {};

      Object.entries(previous).forEach(([key, document]) => {
        const isCoApplicantDocument =
          document?.applicant_role === "CO_APPLICANT" ||
          (removedCoApplicantId &&
            Number(document?.dossier_applicant_id) ===
              Number(removedCoApplicantId)) ||
          key.startsWith("CO_APPLICANT:");

        if (isCoApplicantDocument) {
          changed = true;
          return;
        }

        next[key] = document;
      });

      return changed ? next : previous;
    });

    setDocumentErrors((previous) => {
      const next = {};
      let changed = false;

      Object.entries(previous).forEach(([key, value]) => {
        const isRemovedCoKey =
          key.startsWith("CO_APPLICANT:") ||
          (removedCoApplicantId &&
            key.startsWith(`applicant-${removedCoApplicantId}:`));

        if (isRemovedCoKey) {
          changed = true;
          return;
        }

        next[key] = value;
      });

      return changed ? next : previous;
    });
  }, [backendCoApplicant?.id, coApplicant]);

  const requiredDocumentCount = requiredDocumentSlots.length;
  const uploadedCount = requiredDocumentSlots.filter((slot) =>
    Boolean(uploadedDocuments[slot.key]),
  ).length;
  const allDocumentsUploaded =
    requiredDocumentCount > 0 && uploadedCount === requiredDocumentCount;
  const missingDocumentNames = requiredDocumentSlots
    .filter(slot => !uploadedDocuments[slot.key])
    .map(slot => `${slot.ownerLabel ? `${slot.ownerLabel}: ` : ""}${t(slot.definition.titleKey, slot.definition.code)}`);
  const completionPercentage = requiredDocumentCount
    ? Math.round((uploadedCount / requiredDocumentCount) * 100)
    : 0;
  const serializedDocuments = React.useMemo(
    () => serializeUploadedDocuments(uploadedDocuments),
    [uploadedDocuments],
  );
  React.useEffect(() => {
    if (!loanCaseId) {
      return;
    }
    handleStepValidityChange(currentStepKey, allDocumentsUploaded, missingDocumentNames);
    handleStepValuesChange(currentStepKey, {
      documents: {
        uploaded_documents: serializedDocuments,
      },
    });
  }, [
    allDocumentsUploaded,
    currentStepKey,
    handleStepValidityChange,
    handleStepValuesChange,
    loanCaseId,
    serializedDocuments,
    missingDocumentNames.join("|"),
  ]);
  const handleUpload = React.useCallback(
    async (slot, file) => {
      const documentTypeCode = slot.definition.code;
      const slotKey = slot.key;
      if (!file) return;
      const filename = String(file.name || "");
      const extension = filename.split(".").pop()?.toLowerCase();
      if (!["pdf", "jpg", "jpeg", "png"].includes(extension)) {
        setDocumentErrors(previous => ({...previous, [slotKey]: "Only PDF, JPG and PNG files are supported."}));
        return;
      }
      if (file.size > 20 * 1024 * 1024) {
        setDocumentErrors(previous => ({...previous, [slotKey]: "The file must be 20 MB or smaller."}));
        return;
      }
      try {
        setUploadingCode(slotKey);
        setDocumentErrors((previous) => ({
          ...previous,
          [slotKey]: "",
        }));
        const accessToken = await getAccessTokenSilently();
        const response = await uploadManualLoanCaseDocument({
          loanCaseId,
          documentTypeCode,
          dossierApplicantId: slot.dossierApplicantId || null,
          applicantRole: slot.applicantRole || "",
          file,
          accessToken,
        });
        const responseDocument =
          response.uploaded_document || response.document || response;
        const normalizedResponse = normalizeDocumentRecord(
          responseDocument,
          documentTypeCode,
        );
        const normalized = normalizedResponse
          ? {
              ...normalizedResponse,
              document_type_code: documentTypeCode,
              dossier_applicant_id:
                normalizedResponse.dossier_applicant_id ||
                slot.dossierApplicantId ||
                null,
              applicant_role:
                normalizedResponse.applicant_role || slot.applicantRole || "",
              scope: slot.group,
            }
          : null;
        if (!normalized) {
          throw new Error(
            t(
              "errors.invalidUploadResponse",
              "The server returned an invalid document response.",
            ),
          );
        }
        setUploadedDocuments((previous) => ({
          ...previous,
          [slotKey]: normalized,
        }));
      } catch (uploadError) {
        console.error("Failed to upload manual-entry document:", uploadError);
        setDocumentErrors((previous) => ({
          ...previous,
          [slotKey]:
            uploadError?.data?.errors?.file?.[0] ||
            uploadError?.data?.errors?.dossier_applicant_id?.[0] ||
            uploadError.message ||
            t("errors.uploadFailed", "The document could not be uploaded."),
        }));
      } finally {
        setUploadingCode("");
      }
    },
    [getAccessTokenSilently, loanCaseId, t],
  );
  const handleRemove = React.useCallback(
    async (slot, document) => {
      const documentTypeCode = slot.definition.code;
      const slotKey = slot.key;
      try {
        setDeletingCode(slotKey);
        setDocumentErrors((previous) => ({
          ...previous,
          [slotKey]: "",
        }));
        const accessToken = await getAccessTokenSilently();
        await deleteManualLoanCaseDocument({
          loanCaseId,
          documentTypeCode,
          dossierApplicantId: slot.dossierApplicantId || null,
          applicantRole: slot.applicantRole || "",
          documentId: document.document_id,
          versionId: document.version_id,
          accessToken,
        });
        setUploadedDocuments((previous) => {
          const next = {
            ...previous,
          };
          delete next[slotKey];
          return next;
        });
      } catch (removeError) {
        console.error("Failed to remove manual-entry document:", removeError);
        setDocumentErrors((previous) => ({
          ...previous,
          [slotKey]:
            removeError.message ||
            t("errors.removeFailed", "The document could not be removed."),
        }));
      } finally {
        setDeletingCode("");
      }
    },
    [getAccessTokenSilently, loanCaseId, t],
  );
  const handleDownload = React.useCallback(
    async (document) => {
      if (!document?.version_id) {
        return;
      }
      try {
        const accessToken = await getAccessTokenSilently();
        const blob = await fetchLenderDocumentBlob({
          versionId: document.version_id,
          accessToken,
        });
        const downloadUrl = URL.createObjectURL(blob);
        const link = window.document.createElement("a");
        link.href = downloadUrl;
        link.download = document.original_filename || "document";
        window.document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(downloadUrl);
      } catch (downloadError) {
        console.error("Failed to download document:", downloadError);
      }
    },
    [getAccessTokenSilently],
  );
  return (
    <div className="lender-manual-documents">
      <div className="lender-manual-documents__intro">
        <div className="lender-manual-documents__intro-icon">
          <FileCheck2 size={22} strokeWidth={2.1} />
        </div>
        <div className="lender-manual-documents__intro-copy">
          <h2>{t("manual.title", "Required documents")}</h2>
          <p>
            {t(
              "manual.description",
              "Upload the mandatory documents required for pilot eligibility and OSS renovation planning.",
            )}
          </p>
        </div>
      </div>
      <section className="lender-manual-documents__progress-card">
        <div className="lender-manual-documents__progress-copy">
          <div>
            <span className="lender-manual-documents__progress-label">
              {t("progress.title", "Document completion")}
            </span>
            <strong>
              {uploadedCount} / {requiredDocumentCount}
            </strong>
          </div>
          <span
            className={`lender-manual-documents__completion-chip ${
              allDocumentsUploaded
                ? "lender-manual-documents__completion-chip--complete"
                : ""
            }`}
          >
            {allDocumentsUploaded
              ? t("progress.complete", "Complete")
              : t("progress.incomplete", "Documents missing")}
          </span>
        </div>
        <div
          className="lender-manual-documents__progress-track"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={completionPercentage}
        >
          <span
            className="lender-manual-documents__progress-value"
            style={{
              width: `${completionPercentage}%`,
            }}
          />
        </div>
        {!allDocumentsUploaded ? (
          <p className="lender-manual-documents__progress-help">
            {t(
              "progress.help",
              "All required borrower and property documents must be uploaded before continuing.",
            )}
          </p>
        ) : null}
      </section>
      <div className="lender-manual-documents__list">
        <div style={{ display: "grid", gap: "12px" }}>
          <strong>
            {t("sections.applicantDocuments", "Applicant documents")}
          </strong>
          {applicantDocumentSlots.map((slot) => (
            <DocumentUploadCard
              key={slot.key}
              slot={slot}
              definition={slot.definition}
              document={uploadedDocuments[slot.key] || null}
              isUploading={uploadingCode === slot.key}
              isDeleting={deletingCode === slot.key}
              error={documentErrors[slot.key] || ""}
              onUpload={handleUpload}
              onDownload={handleDownload}
              onRemove={handleRemove}
              locale={i18n.resolvedLanguage || i18n.language || "en-GB"}
              t={t}
            />
          ))}
        </div>
        <div style={{ display: "grid", gap: "12px", marginTop: "18px" }}>
          <strong>
            {t("sections.propertyDocuments", "Property documents")}
          </strong>
          {applicationDocumentSlots.map((slot) => (
            <DocumentUploadCard
              key={slot.key}
              slot={slot}
              definition={slot.definition}
              document={uploadedDocuments[slot.key] || null}
              isUploading={uploadingCode === slot.key}
              isDeleting={deletingCode === slot.key}
              error={documentErrors[slot.key] || ""}
              onUpload={handleUpload}
              onDownload={handleDownload}
              onRemove={handleRemove}
              locale={i18n.resolvedLanguage || i18n.language || "en-GB"}
              t={t}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
